import { useState, useMemo, useCallback, useEffect } from 'react';
import { DictionaryEntry } from '../types';
import { supabase } from '../lib/supabase';

interface UseFilteredDataOptions {
  data: DictionaryEntry[];
  searchQuery: string;
  selectedTag: string;
  selectedLetter: string | null;
}

export const useFilteredData = ({
  data,
  searchQuery,
  selectedTag,
  selectedLetter
}: UseFilteredDataOptions) => {

  const filteredData = useMemo(() => {
    return data.filter(entry => {
      // Filtre par recherche
      const matchesSearch = !searchQuery ||
        entry.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.definition.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.fullDefinition.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      // Filtre par tag
      const matchesTag = selectedTag === 'all' || entry.tags.some(tag => tag.toLowerCase() === selectedTag.toLowerCase());

      // Filtre par lettre
      const matchesLetter = !selectedLetter || entry.letter === selectedLetter;

      // Si aucun filtre n'est actif, n'afficher que les vedettes
      const isFilterActive = searchQuery || (selectedTag !== 'all') || selectedLetter;
      const matchesFeatured = isFilterActive || entry.isFeatured;

      return matchesSearch && matchesTag && matchesLetter && matchesFeatured;
    });
  }, [data, searchQuery, selectedTag, selectedLetter]);

  return filteredData;
};

export const useDictionary = (initialData: DictionaryEntry[] = []) => {
  const [data, setData] = useState<DictionaryEntry[]>(initialData);
  const [loading, setLoading] = useState(initialData.length === 0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const { data: supabaseData, error } = await supabase
      .from('dictionary_entries')
      .select('*')
      .order('term', { ascending: true });

    if (error) {
      console.error('Error fetching dictionary:', error);
      // fallback aux données initiales si erreur (pas idéal mais safe pour le moment)
    } else {
      const mappedData = (supabaseData || []).map(item => ({
        ...item,
        fullDefinition: item.full_definition,
        image: item.image_url,
        isFeatured: item.is_featured,
        likes: item.likes || 0, // Défaut à 0 si null
        // Normalise la lettre pour l'indexation (ex: É -> E)
        letter: (item.letter || item.term[0] || '').normalize('NFD').replace(/[\u0300-\u036f]/g, "").toUpperCase()
      }));
      setData(mappedData);
    }
    setLoading(false);
  };

  const filteredData = useFilteredData({
    data,
    searchQuery,
    selectedTag,
    selectedLetter
  });

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleTagFilter = useCallback((tag: string) => {
    setSelectedTag(tag);
  }, []);

  const handleLetterFilter = useCallback((letter: string | null) => {
    setSelectedLetter(current => current === letter ? null : letter);
  }, []);

  const getRandomEntry = useCallback((): DictionaryEntry | null => {
    if (data.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * data.length);
    return data[randomIndex];
  }, [data]);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedTag('all');
    setSelectedLetter(null);
  }, []);

  return {
    data,
    loading,
    searchQuery,
    selectedTag,
    selectedLetter,
    filteredData,
    handleSearch,
    handleTagFilter,
    handleLetterFilter,
    getRandomEntry,
    clearFilters,
    totalCount: data.length,
    visibleCount: filteredData.length,
    refresh: fetchData
  };
};
